import { Storey } from '@shared/models/design/storey.model';
import { guards } from '@shared/helpers/typeguards.helpers';
import { getHeight } from './mezzanine-utils.helpers';

/**
 * Builds the storey payload for standard (non-mezzanine) updates.
 * @param params
 * @returns next storey or undefined when validation fails
 */
export const buildStandardStorey = ({
  storey,
  name,
  heightDraft,
  elevationDraft,
}: {
  storey: Storey;
  name?: string;
  heightDraft?: number;
  elevationDraft?: number;
}): Storey | undefined => {
  const height = heightDraft ?? 1;
  const elevation = elevationDraft;

  if (!guards.is.number(height)) return undefined;
  if (guards.is.number(elevation) && elevation > 0) return undefined;

  return {
    ...storey,
    name,
    height,
    mezzanine: false,
    mezzanine_elevation: undefined,
    elevation: elevation ?? storey.elevation,
  };
};

/**
 * Extracts draft values from {@link Storey} for form state.
 * @param storey
 * @param isGroundFloor
 * @param parentStoreyHeight
 * @returns draft values
 */
export const getValuesFromStorey = (
  storey: Storey,
  isGroundFloor: boolean,
  parentStoreyHeight?: number,
): {
  name: string;
  height: number;
  elevation: number | undefined;
  mezzanine: boolean;
  mezzanineElevation: number | undefined;
} => {
  const isMezzanine = !isGroundFloor && Boolean(storey.mezzanine);
  const fallbackElevation =
    isMezzanine && parentStoreyHeight ? parentStoreyHeight / 2 : undefined;

  const mezzanineElevation = isMezzanine
    ? (storey.mezzanine_elevation ?? fallbackElevation)
    : undefined;

  const elevation =
    !isGroundFloor || isMezzanine ? (storey.elevation ?? 0) : undefined;

  const height = getHeight(
    { ...storey, mezzanine: isMezzanine },
    parentStoreyHeight,
    mezzanineElevation,
  );

  return {
    name: storey.name ?? '',
    height,
    elevation,
    mezzanine: isMezzanine,
    mezzanineElevation,
  };
};
